import { AxiosResponse } from 'axios';

import Network from './network';
import { SelfData } from '../constants/user';
import { IdentityEndpoints } from '../constants/identity';
import { getRefreshToken } from '../../utilities/common/token';

export interface LoginSuccessData {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

interface AuthHeaderVerify {
  token?: string;
  stateToken: string;
  authCode: string;
  factorType: string;
}

interface biometric {
  token?: string;
  stateToken: string;
}

interface userInfoData {
  name?: string;
  message: string;
  token?: string;
  action: string;
}

interface resetPassword {
  email: string;
  password: string;
  code: string;
}

const { origin } = window.location;
const ssoUrl = `${origin}/sso/success`;

const AUTH_SERVICE_URL = import.meta.env.VITE_API_BASE_URL.replace('/uat', '');

export const signInWithEmailAndPassword = async (
  email: string,
  password: string,
): Promise<LoginSuccessData> => {
  const response = await Network.post({
    url: IdentityEndpoints.login,
    body: { email, password },
  });
  return response.data;
};

export const signInWithSSO = async (
  email: string,
): Promise<{ loginUrl: string; maskedEmail?: string | null }> => {
  const response = await Network.post({
    url: IdentityEndpoints.ssoVerify,
    body: {
      email,
      role: 'hr',
      redirectionUrl: ssoUrl,
    },
  });
  return response.data?.result ?? response.data;
};

export const getTokensFromSSO = async (): Promise<LoginSuccessData> => {
  const response = await Network.post({
    url: IdentityEndpoints.getTokensFromSSO,
    body: {},
  });
  return response.data;
};

export const exchangeRefreshToken = async (): Promise<LoginSuccessData> => {
  const response = await Network.post({
    url: IdentityEndpoints.exchangeRefreshToken,
    body: { refreshToken: getRefreshToken() },
  });
  return response.data;
};

export const exchangeLegacyToken = async (
  legacyToken: string,
): Promise<LoginSuccessData> => {
  const response = await Network.post({
    url: IdentityEndpoints.upgradeLegacyToken,
    body: {},
    headers: { Authorization: legacyToken },
  });
  return response.data;
};

export const self = async (): Promise<AxiosResponse<SelfData>> => {
  return await Network.get({
    url: `${AUTH_SERVICE_URL}user/auth/me`,
    headers: { context: 'provider' },
  });
};

export const revokeAuthToken = async () => {
  await Network.delete({ url: IdentityEndpoints.logout });
};

export const sendCode = async (email: string) =>
  Network.post({
    url: IdentityEndpoints.passwordForgotRequest,
    body: { email },
  });

export const resetPassword = async (resetHeader: resetPassword) =>
  Network.post({
    url: IdentityEndpoints.passwordForgotReset,
    body: {
      email: resetHeader.email,
      password: resetHeader.password,
      resetCode: String(resetHeader.code),
    },
  });

export const authenticate = async (_authHeader?: string) =>
  await Network.post({
    url: `${AUTH_SERVICE_URL}user/mfa/authenticate`,
    headers: { context: 'provider' },
    params: { source: 'web' },
  });

export const verifyCode = async (authHeader: AuthHeaderVerify) => {
  return await Network.post({
    url: `${AUTH_SERVICE_URL}user/mfa/verify`,
    headers: {
      context: 'provider',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: {
      authCode: authHeader.authCode,
      stateToken: authHeader.stateToken,
      factorType: authHeader.factorType,
    },
  });
};

export const supportMessage = async (userInfo: userInfoData) =>
  Network.post({
    url: `${AUTH_SERVICE_URL}marketing/public/report`,
    headers: { context: 'provider' },
    body: {
      name: userInfo.name,
      message: userInfo.message,
      action: userInfo.action,
    },
  });

export const devicePoll = async (authHeader: biometric) =>
  Network.post({
    url: `${AUTH_SERVICE_URL}user/mfa/device/poll`,
    headers: { context: 'provider' },
    body: { stateToken: authHeader.stateToken },
  });

export const resendNotification = async (authHeader: biometric) =>
  Network.post({
    url: `${AUTH_SERVICE_URL}user/mfa/device/resend-notification`,
    headers: { context: 'provider' },
    body: { stateToken: authHeader.stateToken },
  });
